import { useState, useMemo, useCallback } from 'react'
import {
  Paper,
  Text,
  TextInput,
  Checkbox,
  Collapse,
  Badge,
  Group,
  Stack,
  ActionIcon,
} from '@mantine/core'
import { IconSearch, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'
import type { RadiologyProcedure } from './radiologyFormData'
import { RADIOLOGY_COMPENDIUM } from './radiologyFormData'

type Props = {
  selectedProcedures: RadiologyProcedure[]
  onSelectionChange: (procedures: RadiologyProcedure[]) => void
}

export const RadiologyCompendiumSection = ({ selectedProcedures, onSelectionChange }: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedModalities, setExpandedModalities] = useState<Set<string>>(new Set())

  const selectedCodes = useMemo(
    () => new Set(selectedProcedures.map((p) => p.code)),
    [selectedProcedures],
  )

  const filteredCompendium = useMemo(() => {
    if (!searchQuery.trim()) return RADIOLOGY_COMPENDIUM
    const q = searchQuery.toLowerCase()
    return RADIOLOGY_COMPENDIUM.map((modality) => ({
      ...modality,
      procedures: modality.procedures.filter(
        (p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      ),
    })).filter((m) => m.procedures.length > 0)
  }, [searchQuery])

  // When searching, auto-expand all matching modalities
  const effectiveExpanded = useMemo(() => {
    if (searchQuery.trim()) {
      return new Set(filteredCompendium.map((m) => m.key))
    }
    return expandedModalities
  }, [searchQuery, filteredCompendium, expandedModalities])

  const toggleModality = useCallback(
    (key: string) => {
      if (searchQuery.trim()) return // don't toggle while searching
      setExpandedModalities((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    },
    [searchQuery],
  )

  const toggleProcedure = useCallback(
    (proc: RadiologyProcedure) => {
      if (selectedCodes.has(proc.code)) {
        onSelectionChange(selectedProcedures.filter((p) => p.code !== proc.code))
      } else {
        onSelectionChange([...selectedProcedures, proc])
      }
    },
    [selectedCodes, selectedProcedures, onSelectionChange],
  )

  const selectedCountByModality = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const p of selectedProcedures) {
      counts[p.modality] = (counts[p.modality] || 0) + 1
    }
    return counts
  }, [selectedProcedures])

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="lg" className="text-[#6B21A8]">
          SELECT RADIOLOGY PROCEDURES
        </Text>
        {selectedProcedures.length > 0 && (
          <Badge color="blue" variant="filled" size="lg">
            {selectedProcedures.length} selected
          </Badge>
        )}
      </Group>

      <TextInput
        placeholder="Search procedures by name..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        mb="md"
        rightSection={
          searchQuery ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
        rightSectionPointerEvents={searchQuery ? 'all' : 'none'}
      />

      {filteredCompendium.length === 0 && (
        <Text size="sm" c="dimmed" py="md">
          No procedures match your search.
        </Text>
      )}

      <Stack gap={0}>
        {filteredCompendium.map((modality) => {
          const isExpanded = effectiveExpanded.has(modality.key)
          const selectedInModality = selectedCountByModality[modality.label] || 0

          return (
            <div key={modality.key}>
              <Group
                onClick={() => toggleModality(modality.key)}
                className="cursor-pointer rounded-md px-2 py-2 hover:bg-gray-50"
                gap="xs"
                wrap="nowrap"
              >
                {isExpanded ? (
                  <IconChevronDown size={16} className="shrink-0" />
                ) : (
                  <IconChevronRight size={16} className="shrink-0" />
                )}
                <Text fw={600} size="sm">
                  {modality.label}
                </Text>
                <Badge size="xs" variant="light" color="gray">
                  {modality.procedures.length}
                </Badge>
                {selectedInModality > 0 && (
                  <Badge size="xs" variant="filled" color="blue">
                    {selectedInModality} selected
                  </Badge>
                )}
              </Group>

              <Collapse in={isExpanded}>
                <div className="pb-2 pl-8">
                  {modality.procedures.map((proc) => (
                    <Checkbox
                      key={proc.code}
                      label={proc.name}
                      checked={selectedCodes.has(proc.code)}
                      onChange={() => toggleProcedure(proc)}
                      mb={4}
                      size="sm"
                    />
                  ))}
                </div>
              </Collapse>
            </div>
          )
        })}
      </Stack>
    </Paper>
  )
}
